/**
 * MessageBody — safe renderer for a secure-message / reply body.
 *
 * Legacy seeUwell secure messages store HTML in the body; native ClearIntake
 * messages are plain text (composed in a <textarea>). A plain text node renders
 * the HTML ones as raw tags, and blindly setting innerHTML on the plain-text
 * ones would corrupt bodies containing `<`/`&` and expose stored-XSS. So we:
 *   - detect markup, and only then sanitize (DOMPurify) + render as HTML;
 *   - otherwise render as a plain text node with newline-preserving pre-wrap.
 *
 * Sanitisation is defence-in-depth for a HIPAA surface: DOMPurify already strips
 * <script>, event handlers, and `javascript:` URLs; we additionally forbid
 * remote-resource tags (img/iframe/etc.) so a stored body can't beacon that a
 * message was opened, and force every link to open in a new, opener-less tab.
 */
import React from 'react';
import DOMPurify from 'dompurify';

/**
 * "Looks like real HTML" probe — requires a RECOGNISED HTML tag (opening or
 * closing), NOT just any `<x…>`. A denylist-style `/<[a-z].*>/` false-positives
 * on plain-text bodies like `Patient is <stable> per Dr. X` and routes them
 * through the HTML path, which then mangles the text. Legacy seeUwell HTML
 * bodies always carry real tags (`<p>`, `<br>`, `<div>`, `<a …>`, `</td>`, …),
 * so gating on a known tag name keeps plain text on the plain-text path.
 */
// The tag name MUST be followed by a real tag terminator (`>`, `/`, or
// whitespace). Without that lookahead, single-letter tags collide with prose:
// `if a<b, then…` would match `<b` and be treated as HTML. `<b>` matches; `<b,`
// does not.
const HTML_RE =
  /<\/?(?:p|br|div|span|a|b|i|u|em|strong|sub|sup|ul|ol|li|dl|dt|dd|table|thead|tbody|tfoot|tr|td|th|caption|colgroup|col|h[1-6]|blockquote|pre|code|hr|font|center|small|big|s|strike|figure|section|article|img)(?=[\s/>])/i;

// Belt-and-suspenders on top of DOMPurify's default javascript:/on* stripping:
// any surviving anchor opens in a new, opener-less tab. Registered once on the
// shared singleton (module-scope import runs a single time).
DOMPurify.addHook('afterSanitizeAttributes', (node) => {
  if (node.nodeName === 'A') {
    node.setAttribute('target', '_blank');
    node.setAttribute('rel', 'noopener noreferrer');
  }
});

// ALLOW-LIST, not denylist. A denylist over DOMPurify's defaults silently leaves
// every remote-resource loader it doesn't enumerate — SVG <image>/<feImage>, a
// CSS `url()` in a surviving `style` attribute, <video>/<audio>/<source> — each a
// read-receipt/IP/UA beacon that fires when a PHI body is opened. An explicit
// allow-list of the formatting tags legacy bodies actually use closes ALL of them
// at once: nothing that loads a remote resource (no img/svg/media/link/style) and
// no `style`/`src` attribute is permitted, so a stored body cannot beacon or run.
// DOMPurify still strips <script>, on* handlers, and javascript: on top of this.
const SANITIZE_CONFIG = {
  ALLOWED_TAGS: [
    'p', 'br', 'div', 'span', 'a', 'b', 'i', 'u', 'em', 'strong', 'sub', 'sup',
    'ul', 'ol', 'li', 'dl', 'dt', 'dd', 'table', 'thead', 'tbody', 'tfoot', 'tr',
    'td', 'th', 'caption', 'colgroup', 'col', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
    'blockquote', 'pre', 'code', 'hr', 'font', 'center', 'small', 'big', 's', 'strike',
  ],
  // No `style` (CSS url() beacon), no `src` (media beacon) — only inert formatting
  // + link attributes. `href` is further scheme-restricted below.
  ALLOWED_ATTR: ['href', 'title', 'colspan', 'rowspan', 'align', 'valign', 'color', 'face', 'size'],
  // Links may only point at safe schemes (DOMPurify also blocks javascript:/data: by default).
  ALLOWED_URI_REGEXP: /^(?:https?:|mailto:|tel:)/i,
};

interface Props {
  body: string;
  /** Base style (e.g. the caller's bubbleBody). whiteSpace is overridden per branch. */
  style?: React.CSSProperties | undefined;
}

export default function MessageBody({ body, style }: Props): React.ReactElement {
  if (HTML_RE.test(body)) {
    // DOMPurify.sanitize returns a string when RETURN_DOM is not set.
    const clean = DOMPurify.sanitize(body, SANITIZE_CONFIG) as string;
    // HTML already carries its own block structure — pre-wrap would inject the
    // source's inter-tag newlines as blank lines, so force normal wrapping.
    return (
      <div
        style={{ ...style, whiteSpace: 'normal', overflowWrap: 'anywhere' }}
        dangerouslySetInnerHTML={{ __html: clean }}
      />
    );
  }
  // Plain text: keep the newline-preserving behaviour the mailbox always had.
  return <p style={{ ...style, whiteSpace: 'pre-wrap' }}>{body}</p>;
}
